import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../website/components/WebsiteLayout';
import Reveal from '../website/components/Reveal';
import { useLanguage } from '../../hooks/useLanguage';

const MOS_HERO_VIDEO = 'https://thoughtfuloasis-public.s3.us-east-1.amazonaws.com/missionos-hero.mp4';
const MOS_COCREATION_VIDEO =
  'https://thoughtfuloasis-public.s3.us-east-1.amazonaws.com/missionos-vnog-cocreation.mp4';

interface MissionOSContactsProps {
  en: boolean;
}

const MissionOSContacts: React.FC<MissionOSContactsProps> = ({ en }) => (
  <div className="mos-contacts">
    <div className="mos-contact">
      <div className="mos-contact__avatar">
        <img src="/assets/website/missionos/johan.jpg" alt="Johan Seij portrait" />
      </div>
      <div>
        <p className="mos-contact__role">{en ? 'On behalf of VNOG' : 'Namens VNOG'}</p>
        <p className="mos-contact__name">Johan Seij</p>
        <a href="mailto:j.seij@vnog.nl">j.seij@vnog.nl</a>
      </div>
    </div>
    <div className="mos-contact">
      <div className="mos-contact__avatar">
        <img src="/assets/website/missionos/rens.jpg" alt="Rens Dommerholt portrait" />
      </div>
      <div>
        <p className="mos-contact__role">
          {en ? 'On behalf of Thoughtful Oasis' : 'Namens Thoughtful Oasis'}
        </p>
        <p className="mos-contact__name">Rens Dommerholt</p>
        <a href="mailto:rens@thoughtful-oasis.com">rens@thoughtful-oasis.com</a>
      </div>
    </div>
  </div>
);

const MissionOS: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';
  const [activeTab, setActiveTab] = React.useState<'auditable' | 'proactive' | 'summaries'>('auditable');

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">{en ? 'Talk to us' : 'Praat met ons'}</span>
      <h2 className="site-end__title">
        {en
          ? 'Curious what this development could mean for your region?'
          : 'Benieuwd wat deze ontwikkeling voor jouw regio kan betekenen?'}
      </h2>
      <p className="site-end__sub">
        {en ? "We'd love to tell you more." : 'We vertellen er graag meer over.'}
      </p>
      <MissionOSContacts en={en} />
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact?prefill=missionos">
          {en ? 'Get in touch' : 'Neem contact op'}
        </Link>
      </div>
    </div>
  );

  const customPartners = (
    <div className="site-end__partners">
      <p className="site-end__partners-label">Partners</p>
      <div className="site-end__partners-logos">
        <a href="https://www.vnog.nl" target="_blank" rel="noopener noreferrer">
          <img
            src="/assets/website/logos/vnog.png"
            alt="Veiligheidsregio Noord- en Oost-Gelderland (VNOG) — logo"
          />
        </a>
        <a href="https://safetyct.com" target="_blank" rel="noopener noreferrer">
          <img src="/assets/website/logos/safetyct.svg" alt="SafetyCT" />
        </a>
      </div>
    </div>
  );

  return (
    <WebsiteLayout
      bodyClass="missionos"
      footerVariant="missionos"
      customCta={customCta}
      customPartners={customPartners}
    >
      <Helmet>
        <title>MissionOS · Thoughtful Oasis</title>
        <meta
          name="description"
          content="Decision support for incident commanders. Object data, map layers and dispatch notes turned into action perspective."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/mission-os" />
        <meta property="og:type" content="product" />
        <meta property="og:title" content="MissionOS · Thoughtful Oasis" />
        <meta
          property="og:description"
          content="Decision support for incident commanders. Object data, map layers and dispatch notes turned into action perspective. Co-created with VNOG."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/mission-os" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <meta property="og:locale" content={en ? 'en_GB' : 'nl_NL'} />
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "MissionOS",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web, on-premise",
          "description": "Decision support for incident commanders. Object data, map layers and dispatch notes turned into action perspective.",
          "url": "https://thoughtful-oasis.com/mission-os",
          "provider": {
            "@type": "Organization",
            "name": "Thoughtful Oasis",
            "url": "https://thoughtful-oasis.com/"
          }
        }`}</script>
      </Helmet>

      <main className="missionos">
        {/* Series ribbon: MissionOS is powered by oasis_0 · Prometheus. */}
        <Link
          to="/models#series"
          className="ribbon"
          aria-label={en ? 'oasis_0 model series' : 'oasis_0-modelserie'}
        >
          <span className="ribbon__inner">
            <span className="ribbon__dot" aria-hidden="true"></span>
            <span>Powered by oasis_0 · Prometheus</span>
          </span>
        </Link>
        {/* §1 Hero */}
        <header className="mos-hero">
          <div className="container">
            <div className="mos-hero__intro">
              <span className="label">
                {en ? 'In co-creation with VNOG' : 'In co-creatie met VNOG'}
              </span>
              <h1>
                {en ? (
                  <>
                    Intelligence for <span className="accent">decision support</span> in public
                    safety.
                  </>
                ) : (
                  <>
                    Intelligentie voor <span className="accent">beslisondersteuning</span> in de
                    publieke veiligheid.
                  </>
                )}
              </h1>
              <p className="mos-hero__sub">
                {en
                  ? 'MissionOS processes object information, map layers and dispatch notes to support the action perspective. Designed with incident commanders, applicable for all crisis professionals.'
                  : 'MissionOS verwerkt objectinformatie, kaartlagen en kladblokregels om het handelingsperspectief te ondersteunen. Ontworpen met bevelvoerders, bruikbaar voor alle crisisprofessionals.'}
              </p>
              <div className="mos-hero__cta">
                <Link className="btn btn-primary" to="/contact?prefill=missionos-demo">
                  {en ? 'Request a demo' : 'Vraag een demo aan'}
                </Link>
                <a className="btn btn-secondary" href="#tech">
                  {en ? 'See the product' : 'Bekijk het product'}
                </a>
              </div>
            </div>
            <div className="mos-hero__media">
              <video
                src={MOS_COCREATION_VIDEO}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label="MissionOS co-creation session footage with VNOG"
              />
              <div className="mos-hero__overlay">
                <span className="mos-hero__badge">
                  {en ? 'In development' : 'In ontwikkeling'}
                </span>
                <p className="mos-hero__overlay-title">Co-commander</p>
                <p className="mos-hero__overlay-sub">
                  {en ? 'MissionOS for incident commanders' : 'MissionOS voor bevelvoerders'}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* §2 Three levels */}
        <section className="section">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'Three levels' : 'Drie niveaus'}</span>
              <h2>
                {en
                  ? 'Incident response on three levels.'
                  : 'Incidentbestrijding op drie niveaus.'}
              </h2>
            </Reveal>
          </div>
          <Reveal className="mos-levels">
            <div className="mos-level">
              <span className="label">{en ? 'On scene' : 'Ter plaatse'}</span>
              <h3>{en ? 'Direct' : 'Direct'}</h3>
              <p>
                {en
                  ? 'Immediate support at the incident scene, with actionable suppression information for incident commanders.'
                  : 'Directe ondersteuning op het incident, met handelingsgerichte repressie-informatie voor bevelvoerders.'}
              </p>
            </div>
            <div className="mos-level">
              <span className="label">{en ? 'Coordination' : 'Coördinatie'}</span>
              <h3>{en ? 'Tactical' : 'Tactisch'}</h3>
              <p>
                {en
                  ? 'One shared incident perspective, with different action perspectives and functionalities for CaCO, IM, OvD and HOvD.'
                  : 'Eén gedeeld incidentbeeld, met verschillende handelingsperspectieven en functionaliteiten voor CaCo, IM, OvD en HOvD.'}
              </p>
            </div>
            <div className="mos-level">
              <span className="label">{en ? 'Doctrine' : 'Doctrine'}</span>
              <h3>{en ? 'Strategic' : 'Strategisch'}</h3>
              <p>
                {en
                  ? 'A systematic way to make knowledge and experience deployable at every incident, regardless of which professional is on scene.'
                  : 'Een systematische manier om kennis en ervaring inzetbaar te maken bij elk incident, ongeacht welke professional ter plaatse is.'}
              </p>
            </div>
          </Reveal>
        </section>

        {/* §3 Voices from the field */}
        <section className="section mos-dark">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">
                {en ? 'Insights from the field' : 'Inzichten uit het veld'}
              </span>
              <h2>
                {en
                  ? 'Created with domain specialists from the start.'
                  : 'Vanaf het begin gemaakt met domeinexperts.'}
              </h2>
            </Reveal>
            <Reveal>
              <blockquote className="quote">
                <p className="quote__text">
                  {en
                    ? '"MissionOS can help you enormously, when your toolkit isn\'t quite full enough for this incident."'
                    : '"MissionOS kan je enorm helpen, op het moment dat je gereedschapskist net niet vol genoeg is voor dit incident."'}
                </p>
                <cite className="quote__attr">
                  {en ? 'Competence advisor, VRAA' : 'Vakbekwaamheidsadviseur, VRAA'}
                </cite>
              </blockquote>
              <div className="divider--dashed"></div>
              <blockquote className="quote">
                <p className="quote__text">
                  {en
                    ? '"If we want to deploy this operationally with incident commanders, they must be able to fully trust it. How MissionOS was developed earns that trust."'
                    : '"Als we dit operationeel willen inzetten bij bevelvoerders, moeten ze het volledig kunnen vertrouwen. De manier waarop MissionOS ontwikkeld is, verdient dat vertrouwen."'}
                </p>
                <cite className="quote__attr">
                  {en
                    ? 'Richard, data scientist and former incident commander, VNOG'
                    : 'Richard, datawetenschapper en oud-bevelvoerder, VNOG'}
                </cite>
              </blockquote>
              <div className="divider--dashed"></div>
              <blockquote className="quote">
                <p className="quote__text">
                  {en
                    ? '"I see this tool as a proactive pocket guide. As an OVD I\'d definitely use it. As an incident commander, I simply don\'t have the time."'
                    : '"Ik zie deze tool als een proactieve pocketgids. Als OvD zou ik \'m zeker gebruiken. Als bevelvoerder heb ik er gewoon de tijd niet voor."'}
                </p>
                <cite className="quote__attr">{en ? 'OVD, VRK' : 'OvD, VRK'}</cite>
              </blockquote>
            </Reveal>
          </div>
        </section>

        {/* §4 The technology — tabs */}
        <section className="section" id="tech">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'The technology' : 'De techniek'}</span>
              <h2>{en ? 'Proactive and verifiable.' : 'Proactief en verifieerbaar.'}</h2>
              <p className="lede">
                {en
                  ? 'Discover the core technologies behind MissionOS.'
                  : 'Ontdek de kerntechnieken achter MissionOS.'}
              </p>
            </Reveal>
            <Reveal>
              <div className="mos-tabs" role="tablist" aria-label="MissionOS core technologies">
                <button
                  className="mos-tab"
                  role="tab"
                  aria-selected={activeTab === 'auditable'}
                  tabIndex={activeTab === 'auditable' ? 0 : -1}
                  onClick={() => setActiveTab('auditable')}
                >
                  {en ? 'Auditable' : 'Auditeerbaar'}
                </button>
                <button
                  className="mos-tab"
                  role="tab"
                  aria-selected={activeTab === 'proactive'}
                  tabIndex={activeTab === 'proactive' ? 0 : -1}
                  onClick={() => setActiveTab('proactive')}
                >
                  {en ? 'Proactive intelligence' : 'Proactieve intelligentie'}
                </button>
                <button
                  className="mos-tab"
                  role="tab"
                  aria-selected={activeTab === 'summaries'}
                  tabIndex={activeTab === 'summaries' ? 0 : -1}
                  onClick={() => setActiveTab('summaries')}
                >
                  {en ? 'Summaries' : 'Samenvattingen'}
                </button>
              </div>

              <div className="mos-techbox" role="tabpanel" hidden={activeTab !== 'auditable'}>
                <div className="mos-techbox__text">
                  <h3>{en ? 'Auditable' : 'Auditeerbaar'}</h3>
                  <p className="mos-techbox__body">
                    {en
                      ? 'Every action, suggestion or shared insight originates from documents from your region and has a complete audit trail. MissionOS can only search in approved information, such as ARO & ORO cards, manuals and relevant map layers. And MissionOS can only reason based on LCMS and characteristics.'
                      : 'Elke handeling, suggestie of gedeeld inzicht komt voort uit documenten van jouw regio en heeft een volledig auditspoor. MissionOS kan alleen zoeken in goedgekeurde informatie, zoals ARO- en ORO-kaarten, handboeken en relevante kaartlagen. En MissionOS kan alleen redeneren op basis van LCMS en kenmerken.'}
                  </p>
                  <ul>
                    <li>
                      {en
                        ? 'Verifiable and auditable by non-technical domain experts'
                        : 'Verifieerbaar en controleerbaar door niet-technische domeinexperts'}
                    </li>
                    <li>
                      {en
                        ? 'Audit trails applicable during learning sessions'
                        : 'Auditsporen bruikbaar tijdens leersessies'}
                    </li>
                  </ul>
                </div>
                <div className="mos-techbox__shot">
                  <img
                    src="/assets/website/missionos/audit-trail.jpg"
                    alt="MissionOS audit trail — every suggestion linked to its source document"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="mos-techbox" role="tabpanel" hidden={activeTab !== 'proactive'}>
                <div className="mos-techbox__text">
                  <h3>{en ? 'Proactive intelligence' : 'Proactieve intelligentie'}</h3>
                  <p className="mos-techbox__body">
                    {en
                      ? "MissionOS activates from the first dispatch and proactively provides information and suggestions. During the initial response, you don't need any buttons: MissionOS enters notepad lines as you're used to."
                      : 'MissionOS activeert vanaf de eerste alarmering en biedt proactief informatie en suggesties. Tijdens de eerste inzet heb je geen knoppen nodig: MissionOS plaatst kladblokregels zoals je gewend bent.'}
                  </p>
                  <ul>
                    <li>
                      {en
                        ? 'Information supporting the action perspective'
                        : 'Informatie die het handelingsperspectief ondersteunt'}
                    </li>
                    <li>
                      {en ? 'Real-time suppression information' : 'Real-time repressie-informatie'}
                    </li>
                  </ul>
                </div>
                <div className="mos-techbox__shot">
                  <img
                    src="/assets/website/missionos/proactive.jpg"
                    alt="MissionOS proactively surfacing suppression information during a dispatch"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="mos-techbox" role="tabpanel" hidden={activeTab !== 'summaries'}>
                <div className="mos-techbox__text">
                  <h3>{en ? 'Summaries' : 'Samenvattingen'}</h3>
                  <p className="mos-techbox__body">
                    {en
                      ? 'MissionOS can generate a summary of the complete incident at any time on request. This summary is verified with object information and dispatch notes, displayed in the characteristics schema based on facts and unknowns.'
                      : 'MissionOS kan op verzoek een samenvatting van het volledige incident genereren. Die samenvatting is geverifieerd met objectinformatie en kladblokregels, weergegeven in het kenmerkenschema op basis van feiten en onbekenden.'}
                  </p>
                  <ul>
                    <li>
                      {en
                        ? 'Incident overview in the same reasoning framework'
                        : 'Incidentbeeld in hetzelfde redeneerkader'}
                    </li>
                    <li>
                      {en
                        ? 'Concrete starting point for potential escalation'
                        : 'Concreet startpunt bij mogelijke opschaling'}
                    </li>
                  </ul>
                </div>
                <div className="mos-techbox__shot">
                  <img
                    src="/assets/website/missionos/samenvatting.jpg"
                    alt="MissionOS incident summary in the characteristics schema"
                    loading="lazy"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* §5 Roles */}
        <section className="section">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'Roles' : 'Rollen'}</span>
              <h2>
                {en
                  ? 'Effective support for every role.'
                  : 'Effectieve ondersteuning voor elke rol.'}
              </h2>
            </Reveal>
          </div>
          <Reveal className="mos-roles">
            <div className="product-card">
              <p className="product-card__label">
                {en ? 'Incident Commanders' : 'Bevelvoerders'}
              </p>
              <p className="product-card__body">
                {en
                  ? 'Reliable insights for a sharpened action perspective.'
                  : 'Betrouwbare inzichten voor een scherper handelingsperspectief.'}
              </p>
            </div>
            <div className="product-card">
              <p className="product-card__label">
                {en ? 'Information Managers' : 'Informatiemanagers'}
              </p>
              <p className="product-card__body">
                {en
                  ? 'A verified starting point and filled canvas before the first CoPI meeting.'
                  : 'Een geverifieerd startpunt en een ingevuld canvas vóór de eerste CoPI-vergadering.'}
              </p>
            </div>
            <div className="product-card">
              <p className="product-card__label">{en ? 'CaCO' : 'CaCo'}</p>
              <p className="product-card__body">
                {en
                  ? 'Support in processing map layers and summarizing them in templates.'
                  : 'Ondersteuning bij het verwerken van kaartlagen en het samenvatten ervan in templates.'}
              </p>
            </div>
            <div className="product-card">
              <p className="product-card__label">{en ? 'OVD' : 'OvD'}</p>
              <p className="product-card__body">
                {en
                  ? 'Deeper insight into the deployment of your incident commanders, and an inexhaustible resource.'
                  : 'Dieper inzicht in de inzet van je bevelvoerders, en een onuitputtelijke kennisbron.'}
              </p>
            </div>
            <div className="product-card">
              <p className="product-card__label">
                {en ? 'Dispatch Center' : 'Meldkamer'}
              </p>
              <p className="product-card__body">
                {en
                  ? 'Identifying potential blind spots and sharpening your inquiry.'
                  : 'Mogelijke blinde vlekken signaleren en je uitvraag scherper maken.'}
              </p>
            </div>
          </Reveal>
        </section>

        {/* §6 In action */}
        <section className="section">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'In the field' : 'In het veld'}</span>
              <h2>{en ? 'See MissionOS in action.' : 'Zie MissionOS in actie.'}</h2>
              <p className="lede">
                {en
                  ? 'Innovation through field validation and conversations with the profession.'
                  : 'Innovatie door veldvalidatie en gesprekken met het vak.'}
              </p>
            </Reveal>
            <Reveal className="mos-action">
              <article className="mos-mediacard">
                <div className="mos-mediacard__media">
                  <img
                    src="/assets/website/missionos/fieldtesting.webp"
                    alt="Firefighters during a MissionOS field test"
                    loading="lazy"
                  />
                </div>
                <div className="mos-mediacard__text">
                  <span className="status status--live">
                    {en ? 'Live test' : 'Live test'}
                  </span>
                  <h3>{en ? 'Field Tests 2026' : 'Veldtests 2026'}</h3>
                  <p>
                    {en
                      ? 'In September, MissionOS will be tested in a public simulation. Want to attend? Get in touch for more information.'
                      : 'In september wordt MissionOS getest in een publieke simulatie. Erbij zijn? Neem contact op voor meer informatie.'}
                  </p>
                </div>
              </article>
              <article className="mos-mediacard">
                <div className="mos-mediacard__media">
                  <video
                    src={MOS_HERO_VIDEO}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label="MissionOS in operation"
                  />
                </div>
                <div className="mos-mediacard__text">
                  <span className="status status--research">
                    {en ? 'Network' : 'Netwerk'}
                  </span>
                  <h3>eRIC</h3>
                  <p>
                    {en
                      ? 'Receive a live demo of MissionOS at eRIC 2026, Enschede. Get in touch for more information.'
                      : 'Krijg een live demo van MissionOS op eRIC 2026, Enschede. Neem contact op voor meer informatie.'}
                  </p>
                </div>
              </article>
              <article className="mos-mediacard">
                <div className="mos-mediacard__media">
                  <img
                    src="/assets/website/missionos/interschutz.webp"
                    alt="Interschutz trade fair"
                    loading="lazy"
                  />
                </div>
                <div className="mos-mediacard__text">
                  <span className="status status--research">{en ? 'Market' : 'Markt'}</span>
                  <h3>Interschutz</h3>
                  <p>
                    {en
                      ? 'Receive a live demo of MissionOS at Interschutz 2026, Hannover. Get in touch for more information.'
                      : 'Krijg een live demo van MissionOS op Interschutz 2026, Hannover. Neem contact op voor meer informatie.'}
                  </p>
                </div>
              </article>
            </Reveal>
            <Reveal as="p" style={{ textAlign: 'center', marginTop: 48 }}>
              <Link className="btn btn-primary" to="/contact?prefill=missionos-demo">
                {en ? 'Get in touch' : 'Neem contact op'}
              </Link>
            </Reveal>
          </div>
        </section>

        {/* §7 Co-creation */}
        <section className="section">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'Co-creation with VNOG' : 'Co-creatie met VNOG'}</span>
              <h2>
                {en
                  ? 'Built with the profession, from the first prototype.'
                  : 'Met het vak gebouwd, vanaf het eerste prototype.'}
              </h2>
            </Reveal>
          </div>
          <Reveal className="mos-coc">
            <div className="mos-coc__top">
              <div className="mos-coc__tile mos-coc__tile--video">
                <video
                  src={MOS_COCREATION_VIDEO}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label="MissionOS co-creation with VNOG"
                />
              </div>
              <div className="mos-coc__tile mos-coc__tile--explain">
                <span className="label">{en ? 'Why this works' : 'Waarom dit werkt'}</span>
                <h3>
                  {en ? 'The profession in the loop, from day one.' : 'Het vak in de loop, vanaf dag één.'}
                </h3>
                <p>
                  {en
                    ? 'Since 2024 we have worked intensively with VNOG. Domain expertise plus technical knowledge makes the product possible; the way we collaborate makes it enjoyable.'
                    : 'Sinds 2024 werken we intensief samen met VNOG. Domeinkennis plus technische kennis maakt het product mogelijk; de manier waarop we samenwerken maakt het leuk.'}
                </p>
              </div>
            </div>
            <div className="mos-coc__phases">
              <div className="mos-coc__tile">
                <span className="label">01 · Proof of Concept</span>
                <h3>{en ? 'The concept works.' : 'Het concept werkt.'}</h3>
                <p>
                  {en
                    ? 'Early 2025 proved that the MissionOS concept works and can be of added value for incident commanders.'
                    : 'Begin 2025 bewees dat het MissionOS-concept werkt en toegevoegde waarde kan hebben voor bevelvoerders.'}
                </p>
                <span className="mos-coc__stat">
                  {en
                    ? '88+ simulations · 15+ colleagues'
                    : "88+ simulaties · 15+ collega's"}
                </span>
              </div>
              <div className="mos-coc__tile">
                <span className="label">02 · Prototype</span>
                <h3>{en ? 'Ready for practice.' : 'Klaar voor de praktijk.'}</h3>
                <p>
                  {en
                    ? 'In continued development, MissionOS is being prepared for testing in practice. All disciplines within the organization are involved.'
                    : 'In doorontwikkeling wordt MissionOS klaargemaakt voor tests in de praktijk. Alle disciplines binnen de organisatie zijn erbij betrokken.'}
                </p>
              </div>
              <div className="mos-coc__tile">
                <span className="label">03 · MVP</span>
                <h3>Co-commander.</h3>
                <p>
                  {en
                    ? 'The planned launch of Co-commander, MissionOS for incident commanders, end of 2025.'
                    : 'De geplande lancering van Co-commander, MissionOS voor bevelvoerders, eind 2025.'}
                </p>
              </div>
            </div>
          </Reveal>
        </section>

        {/* §8 Collaboration models */}
        <section className="section">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">
                {en ? 'Collaboration models' : 'Samenwerkingsmodellen'}
              </span>
              <h2>
                {en
                  ? 'Flexible solutions for every organizational need.'
                  : 'Flexibele oplossingen voor elke organisatorische behoefte.'}
              </h2>
            </Reveal>
            <Reveal className="compare">
              <div className="compare__col compare__col--alt">
                <p className="compare__head">{en ? 'API only' : 'Alleen API'}</p>
                <h3 className="compare__title">
                  {en ? 'Our intelligence in your platform.' : 'Onze intelligentie in jouw platform.'}
                </h3>
                <p className="compare__body">
                  {en
                    ? 'For organizations that want to integrate our AI intelligence and Vector Ontology into their own existing dashboard or operational platform.'
                    : 'Voor organisaties die onze AI-intelligentie en Vector-Ontologie willen integreren in hun bestaande dashboard of operationele platform.'}
                </p>
                <ul className="mos-model-list">
                  <li>{en ? 'RESTful API documentation' : 'RESTful API-documentatie'}</li>
                  <li>{en ? 'Scalable AI endpoints' : 'Schaalbare AI-endpoints'}</li>
                  <li>{en ? 'Developer-first support' : 'Developer-first support'}</li>
                </ul>
                <Link className="btn btn-primary" to="/contact?prefill=missionos-api">
                  {en ? 'Get in touch' : 'Neem contact op'}
                </Link>
              </div>
              <div className="compare__col compare__col--dark">
                <p className="compare__head">
                  {en ? 'Full implementation' : 'Volledige implementatie'}
                </p>
                <h3 className="compare__title">
                  {en ? 'A complete, turn-key solution.' : 'Een complete, turn-key oplossing.'}
                </h3>
                <p className="compare__body">
                  {en
                    ? 'Software, seamless system integration and tailored training for safety regions that want to get started immediately.'
                    : "Software, naadloze systeemintegratie en op maat gemaakte training voor veiligheidsregio's die direct aan de slag willen."}
                </p>
                <ul className="mos-model-list">
                  <li>{en ? 'Turn-key software suite' : 'Turn-key softwarepakket'}</li>
                  <li>
                    {en ? 'On-site training & support' : 'Training & ondersteuning op locatie'}
                  </li>
                  <li>{en ? 'Full backend integration' : 'Volledige backend-integratie'}</li>
                </ul>
                <Link className="btn btn-primary" to="/contact?prefill=missionos-turnkey">
                  {en ? 'Get in touch' : 'Neem contact op'}
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* §9 FAQ */}
        <section className="section">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">FAQ</span>
              <h2>{en ? 'Frequently asked questions.' : 'Veelgestelde vragen.'}</h2>
            </Reveal>
            <Reveal style={{ maxWidth: 'var(--max-w-prose)', margin: '0 auto' }}>
              {[
                {
                  q: en
                    ? 'How do you account for Human Factors?'
                    : 'Hoe houden jullie rekening met Human Factors?',
                  a: en
                    ? 'With MissionOS we actually try to share less information that is more relevant. MissionOS will therefore be part of a study on human factors for incident commanders.'
                    : 'Met MissionOS proberen we juist mínder maar relevantere informatie te delen. MissionOS wordt daarom onderdeel van een studie naar human factors bij bevelvoerders.',
                },
                {
                  q: en
                    ? 'Is MissionOS only useful during the response phase?'
                    : 'Is MissionOS alleen bruikbaar tijdens de uitrukfase?',
                  a: en
                    ? 'Definitely not. The incident can change even after the first unit arrives on scene. Dispatch communication continues and so MissionOS keeps analyzing and advising. The second unit or response vehicle can continue working with that information.'
                    : 'Zeker niet. Het incident kan ook veranderen nadat de eerste eenheid ter plaatse is. Meldkamer-communicatie gaat door en daarom blijft MissionOS analyseren en adviseren. De tweede eenheid of het volgvoertuig kan met die informatie verder.',
                },
                {
                  q: en
                    ? 'How do you deal with hallucinations?'
                    : 'Hoe gaan jullie om met hallucinaties?',
                  a: en
                    ? 'Hallucinations mainly arise from continuously reasoning on a single task. We prevent this by splitting the language models per task and forcing them to reason on approved information.'
                    : 'Hallucinaties ontstaan vooral uit het continu redeneren op één taak. Wij voorkomen dat door de taalmodellen per taak te splitsen en ze te dwingen te redeneren op goedgekeurde informatie.',
                },
                {
                  q: en
                    ? 'Is this tool suitable for other roles?'
                    : 'Is deze tool geschikt voor andere rollen?',
                  a: en
                    ? 'Yes. Every role deals with the same incident but expects different functions from MissionOS. We are researching and developing this for IM & OVD.'
                    : 'Ja. Elke rol heeft met hetzelfde incident te maken maar verwacht andere functies van MissionOS. We onderzoeken en ontwikkelen dat voor IM & OvD.',
                },
                {
                  q: en ? 'What makes MissionOS unique?' : 'Wat maakt MissionOS uniek?',
                  a: en
                    ? 'It works exclusively with validated, organization-owned documentation and combines six AI models to filter and structure information without hallucinations.'
                    : 'Het werkt uitsluitend met gevalideerde documentatie van de organisatie zelf en combineert zes AI-modellen om informatie zonder hallucinaties te filteren en te structureren.',
                },
                {
                  q: en ? 'When is the commercial launch?' : 'Wanneer is de commerciële lancering?',
                  a: en
                    ? 'We expect to offer the product to an exclusive group of safety regions by end of 2025.'
                    : "We verwachten het product eind 2025 aan een exclusieve groep veiligheidsregio's aan te bieden.",
                },
              ].map((item, idx) => (
                <details className="disclosure" key={idx}>
                  <summary className="disclosure__summary">
                    <div>
                      <p className="disclosure__title">{item.q}</p>
                    </div>
                    <span className="disclosure__icon" aria-hidden="true"></span>
                  </summary>
                  <div className="disclosure__body">
                    <p className="disclosure__desc">{item.a}</p>
                  </div>
                </details>
              ))}
            </Reveal>
          </div>
        </section>

      </main>
    </WebsiteLayout>
  );
};

export default MissionOS;
